import React, { FC } from 'react'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { ITrackingStatus } from 'types/shipment'
import { TrackingIcon } from 'transfix-icons'
import { trackingRequirementFullDescription } from './constants/constants'
import { SHIPMENT_TRACKING_LEVELS } from './types'
import { formatTrackingSegment } from './utils'
import 'scss/5_components/_tracking-tag.scss'

export interface ITrackingProps {
  shipperTrackingSegment?: {
    description: string
    id: string
    level: SHIPMENT_TRACKING_LEVELS
  }
  trackingStatus?: ITrackingStatus
  /**
   * If you want to use a context card instead of the default message
   */
  isContextCard?: boolean
  tooltipPosition?: 'top' | 'bottom' | 'right' | 'left'
}

export const OPTIONS = {
  not_tracking_not_required: {
    text: 'Not tracking',
    color: TAG_STYLE.dark,
  },
  not_tracking_required: {
    text: 'Not tracking \u2022 Tracking Required',
    color: TAG_STYLE.error,
  },
  currently_tracking: {
    text: 'Currently Tracking via',
    color: TAG_STYLE.info,
  },
  was_tracking: {
    text: 'Was Tracking via',
    color: TAG_STYLE.warning,
  },
}

export const TrackingTag: FC<ITrackingProps> = ({
  isContextCard,
  shipperTrackingSegment,
  tooltipPosition = isContextCard ? 'bottom' : 'top',
  trackingStatus,
}) => {
  const { trackingMethod, trackingState } = trackingStatus || {
    trackingState: 'not_tracking_not_required',
    trackingMethod: 'App',
  }
  const { level } = shipperTrackingSegment || {}
  const { color = 'color', text = '' } = OPTIONS[trackingState]
  const formattedTrackingSegment = formatTrackingSegment(shipperTrackingSegment)
  const isTracking = ['currently_tracking', 'was_tracking'].includes(trackingState)
  const tooltipTitle = `${text} ${isTracking ? trackingMethod : ''}`
  const tagLevel = level ? ` · L${level}` : ''

  return (
    <div className='flex items-center mr2 tracking-tag'>
      <Tooltip
        position={tooltipPosition}
        trigger={
          <div>
            <Tag
              icon={<TrackingIcon size='medium' />}
              size={TAG_SIZE.xsmall}
              outlineOnly={trackingState.includes('not_tracking_not_required')}
              label={
                <>
                  <span>{trackingMethod}</span>
                  <span className='normal'>{tagLevel}</span>
                </>
              }
              tagStyle={TAG_STYLE[color as keyof typeof TAG_STYLE]}
              style={{ whiteSpace: 'nowrap' }}
            />
          </div>
        }>
        {isContextCard ? (
          'Click to view more details about tracking'
        ) : (
          <div className='tracking-tag-tooltip'>
            <p className='b'>{tooltipTitle}</p>
            {level && (
              <div className='tracking-tag-tooltip__definition text-small'>
                <p>
                  <span className='b'>{formattedTrackingSegment?.title}</span>
                  <br />
                  {trackingRequirementFullDescription.get(level)}
                </p>
              </div>
            )}
          </div>
        )}
      </Tooltip>
    </div>
  )
}

export default TrackingTag
